const START_DATE_FIELD_LABEL_SUFFIX = '.StartDate';
const END_DATE_FIELD_LABEL_SUFFIX = '.EndDate';
const FIELD_LABEL_PATTERN_START_DATE = /^(.*)\.StartDate$/i;
const FIELD_LABEL_PATTERN_END_DATE = /^(.*)\.EndDate$/i;

export const extractDateRangeFieldLabel = (fieldLabel: string): string =>
  (fieldLabel.match(FIELD_LABEL_PATTERN_START_DATE) ||
    fieldLabel.match(FIELD_LABEL_PATTERN_END_DATE))?.[1] ?? '';

export const fieldLabelIsDateRangeStartField = (fieldLabel: string) =>
  fieldLabel.slice(-START_DATE_FIELD_LABEL_SUFFIX.length) ===
  START_DATE_FIELD_LABEL_SUFFIX;

export const fieldLabelIsDateRangeEndField = (fieldLabel: string) =>
  fieldLabel.slice(-END_DATE_FIELD_LABEL_SUFFIX.length) ===
  END_DATE_FIELD_LABEL_SUFFIX;

export const fieldLabelIsPartOfDateRangeField = (fieldLabel: string) =>
  fieldLabelIsDateRangeStartField(fieldLabel) ||
  fieldLabelIsDateRangeEndField(fieldLabel);

export const toStartDateLabel = (fieldLabel: string) =>
  `${fieldLabel}${START_DATE_FIELD_LABEL_SUFFIX}`;

export const toEndDateLabel = (fieldLabel: string) =>
  `${fieldLabel}${END_DATE_FIELD_LABEL_SUFFIX}`;

const formatStartDateFieldLabel = (label: string) =>
  label.replace(FIELD_LABEL_PATTERN_START_DATE, '$1 - Start');

const formatEndDateFieldLabel = (label: string) =>
  label.replace(FIELD_LABEL_PATTERN_END_DATE, '$1 - End');

export const dashFormatDateRangeFieldLabel = (label: string) =>
  fieldLabelIsDateRangeStartField(label)
    ? formatStartDateFieldLabel(label)
    : fieldLabelIsDateRangeEndField(label)
      ? formatEndDateFieldLabel(label)
      : label;

/**
 * Compare two labels in order to sort start date time in front of end start date
 */
export const compareDateRangeFieldLabels = (
  fieldLabelA: string,
  fieldLabelB: string
) => {
  if (
    extractDateRangeFieldLabel(fieldLabelA) ===
    extractDateRangeFieldLabel(fieldLabelB)
  ) {
    return fieldLabelIsDateRangeStartField(fieldLabelA) &&
      fieldLabelIsDateRangeEndField(fieldLabelB)
      ? -1
      : fieldLabelIsDateRangeStartField(fieldLabelB) &&
          fieldLabelIsDateRangeEndField(fieldLabelA)
        ? 1
        : 0;
  }
  return 0;
};

export const getDateRangeFieldLabelPairsFromFieldLabels = (
  fieldLabels: string[]
): Record<string, { start: string; end: string }> =>
  Object.entries(
    fieldLabels.reduce<Record<string, { start?: string; end?: string }>>(
      // find all potential pairs
      (potentialPairs, fieldLabel) => {
        if (fieldLabelIsPartOfDateRangeField(fieldLabel)) {
          const dateRangeFieldLabel = extractDateRangeFieldLabel(fieldLabel);
          const fieldsObject = fieldLabelIsDateRangeStartField(fieldLabel)
            ? { start: fieldLabel }
            : { end: fieldLabel };

          return {
            ...potentialPairs,
            [dateRangeFieldLabel]: potentialPairs[dateRangeFieldLabel]
              ? {
                  ...potentialPairs[dateRangeFieldLabel],
                  ...fieldsObject,
                }
              : fieldsObject,
          };
        }
        return potentialPairs;
      },
      {}
    )
  ).reduce<Record<string, { start: string; end: string }>>(
    // return only pairs with both start and end
    (pairs, [fieldLabel, { start, end }]) =>
      start && end ? { ...pairs, [fieldLabel]: { start, end } } : pairs,
    {}
  );
